import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { extractErrorMessage } from '../utils/errorHandler';
import './CategoryManager.css';

interface CategoryItem {
  id: number;
  name: string;
  product_type_id?: number;
  category_id?: number;
  description?: string;
  product_type?: {
    id: number;
    name: string;
  };
  category?: {
    id: number;
    name: string;
  };
}

const CategoryManager: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<'subcategories' | 'features' | 'grapes'>('subcategories');
  
  // Data states
  const [subcategories, setSubcategories] = useState<CategoryItem[]>([]);
  const [features, setFeatures] = useState<CategoryItem[]>([]);
  const [grapes, setGrapes] = useState<CategoryItem[]>([]);
  const [productTypes, setProductTypes] = useState<CategoryItem[]>([]);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  
  // Filter states
  const [selectedProductType, setSelectedProductType] = useState<number | ''>('');
  const [selectedCategory, setSelectedCategory] = useState<number | ''>('');
  
  // Edit states
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Fetch product types (needed for filtering)
  const fetchProductTypes = useCallback(async () => {
    try {
      const response = await axios.get('/api/product-types');
      setProductTypes(response.data);
    } catch (err) {
      console.error('Error fetching product types:', err);
      setError(extractErrorMessage(err));
    }
  }, []);

  // Fetch categories (needed for subcategory management)
  const fetchCategories = useCallback(async () => {
    try {
      let url = '/api/categories';
      if (selectedProductType) {
        url += `?product_type_id=${selectedProductType}`;
      }
      const response = await axios.get(url);
      setCategories(response.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setError(extractErrorMessage(err));
    }
  }, [selectedProductType]);

  // Fetch subcategories based on selected category
  const fetchSubcategories = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/api/subcategories';
      if (selectedCategory) {
        url += `?category_id=${selectedCategory}`;
      }
      const response = await axios.get(url);
      setSubcategories(response.data);
    } catch (err) {
      console.error('Error fetching subcategories:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  // Fetch product features based on selected product type
  const fetchFeatures = useCallback(async () => {
    try {
      setLoading(true);
      let url = '/api/product-features';
      if (selectedProductType) {
        url += `?product_type_id=${selectedProductType}`;
      }
      const response = await axios.get(url);
      setFeatures(response.data);
    } catch (err) {
      console.error('Error fetching product features:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [selectedProductType]);

  // Fetch grape varieties
  const fetchGrapeVarieties = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/grape-varieties');
      setGrapes(response.data);
    } catch (err) {
      console.error('Error fetching grape varieties:', err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  // Delete handlers
  const handleDelete = async (type: string, id: number) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten element?')) {
      return;
    }

    try {
      setLoading(true);
      let url = '';
      
      switch (type) {
        case 'subcategory':
          url = `/api/subcategories/${id}`;
          break;
        case 'feature':
          url = `/api/product-features/${id}`;
          break;
        case 'grape':
          url = `/api/grape-varieties/${id}`;
          break;
      }
      
      await axios.delete(url);
      
      // Refresh the appropriate list
      if (type === 'subcategory') fetchSubcategories();
      else if (type === 'feature') fetchFeatures();
      else if (type === 'grape') fetchGrapeVarieties();
      
      setSuccess(`Element został pomyślnie usunięty`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error(`Error deleting ${type}:`, err);
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Edit handlers
  const startEdit = (id: number, name: string) => {
    setEditId(id);
    setEditName(name);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditName('');
  };

  const saveEdit = async (type: string, id: number) => {
    if (!editName.trim()) {
      setError('Nazwa nie może być pusta');
      return;
    }

    try {
      setLoading(true);
      let url = '';
      
      switch (type) {
        case 'subcategory':
          url = `/api/subcategories/${id}`;
          break;
        case 'feature':
          url = `/api/product-features/${id}`;
          break;
        case 'grape':
          url = `/api/grape-varieties/${id}`;
          break;
      }
      
      // Properly encode special characters in the request
      await axios.put(url, { 
        name: editName.trim(),
        original_name: subcategories.find(s => s.id === id)?.name || '' 
      });
      
      // Refresh the appropriate list
      if (type === 'subcategory') fetchSubcategories();
      else if (type === 'feature') fetchFeatures();
      else if (type === 'grape') fetchGrapeVarieties();
      
      setSuccess('Zmiana nazwy zakończona pomyślnie');
      setTimeout(() => setSuccess(''), 3000);
      
      // Reset edit state
      setEditId(null);
      setEditName('');
    } catch (err) {
      console.error(`Error updating ${type}:`, err);
      // Enhance error handling to provide more specific messages
      const errorMsg = extractErrorMessage(err);
      if (errorMsg.includes('404') || errorMsg.includes('Not Found')) {
        setError(`Endpoint not found. There might be an issue with special characters in the name.`);
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    fetchProductTypes();
  }, [fetchProductTypes]);

  // Load categories when product type changes
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories, selectedProductType]);

  // Load data based on active tab and filters
  useEffect(() => {
    if (activeTab === 'subcategories' && selectedCategory) {
      fetchSubcategories();
    } else if (activeTab === 'features' && selectedProductType) {
      fetchFeatures();
    } else if (activeTab === 'grapes') {
      fetchGrapeVarieties();
    }
  }, [activeTab, selectedCategory, selectedProductType, fetchSubcategories, fetchFeatures, fetchGrapeVarieties]);

  return (
    <div className="category-manager-container">
      <h1>Zarządzanie kategoriami</h1>
      
      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError('')} className="close-btn">×</button>
        </div>
      )}
      
      {success && (
        <div className="success-message">
          {success}
          <button onClick={() => setSuccess('')} className="close-btn">×</button>
        </div>
      )}
      
      <div className="tabs">
        <button 
          className={`tab-btn ${activeTab === 'subcategories' ? 'active' : ''}`}
          onClick={() => setActiveTab('subcategories')}
        >
          Podkategorie
        </button>
        <button 
          className={`tab-btn ${activeTab === 'features' ? 'active' : ''}`}
          onClick={() => setActiveTab('features')}
        >
          Cechy produktów
        </button>
        <button 
          className={`tab-btn ${activeTab === 'grapes' ? 'active' : ''}`}
          onClick={() => setActiveTab('grapes')}
        >
          Odmiany winogron
        </button>
      </div>
      
      <div className="tab-content">
        {activeTab === 'subcategories' && (
          <>
            <div className="filters">
              <div className="filter-group">
                <label>Typ produktu:</label>
                <select 
                  value={selectedProductType} 
                  onChange={(e) => {
                    setSelectedProductType(e.target.value ? Number(e.target.value) : '');
                    setSelectedCategory(''); // Reset category when product type changes
                  }}
                >
                  <option value="">Wszystkie typy produktów</option>
                  {productTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>Kategoria:</label>
                <select 
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value ? Number(e.target.value) : '')}
                  disabled={!selectedProductType}
                >
                  <option value="">Wybierz kategorię</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>{category.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {loading ? (
              <div className="loading">Ładowanie...</div>
            ) : selectedCategory ? (
              subcategories.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nazwa</th>
                      <th>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subcategories.map(subcategory => (
                      <tr key={subcategory.id}>
                        <td>
                          {editId === subcategory.id ? (
                            <input 
                              type="text" 
                              value={editName} 
                              onChange={(e) => setEditName(e.target.value)}
                              className="edit-input"
                            />
                          ) : (
                            subcategory.name
                          )}
                        </td>
                        <td>
                          {editId === subcategory.id ? (
                            <div className="action-buttons">
                              <button 
                                onClick={() => saveEdit('subcategory', subcategory.id)}
                                className="save-btn"
                                disabled={loading}
                              >
                                Zapisz
                              </button>
                              <button 
                                onClick={cancelEdit}
                                className="cancel-btn"
                              >
                                Anuluj
                              </button>
                            </div>
                          ) : (
                            <div className="action-buttons">
                              <button 
                                onClick={() => startEdit(subcategory.id, subcategory.name)}
                                className="edit-btn"
                              >
                                Edytuj
                              </button>
                              <button 
                                onClick={() => handleDelete('subcategory', subcategory.id)}
                                className="delete-btn"
                                disabled={loading}
                              >
                                Usuń
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-data">Brak podkategorii dla wybranej kategorii</div>
              )
            ) : (
              <div className="no-selection">Wybierz kategorię, aby zobaczyć podkategorie</div>
            )}
          </>
        )}
        
        {activeTab === 'features' && (
          <>
            <div className="filters">
              <div className="filter-group">
                <label>Typ produktu:</label>
                <select 
                  value={selectedProductType} 
                  onChange={(e) => setSelectedProductType(e.target.value ? Number(e.target.value) : '')}
                >
                  <option value="">Wybierz typ produktu</option>
                  {productTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {loading ? (
              <div className="loading">Ładowanie...</div>
            ) : selectedProductType ? (
              features.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nazwa</th>
                      <th>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {features.map(feature => (
                      <tr key={feature.id}>
                        <td>
                          {editId === feature.id ? (
                            <input 
                              type="text" 
                              value={editName} 
                              onChange={(e) => setEditName(e.target.value)}
                              className="edit-input"
                            />
                          ) : (
                            feature.name
                          )}
                        </td>
                        <td>
                          {editId === feature.id ? (
                            <div className="action-buttons">
                              <button 
                                onClick={() => saveEdit('feature', feature.id)}
                                className="save-btn"
                                disabled={loading}
                              >
                                Zapisz
                              </button>
                              <button 
                                onClick={cancelEdit}
                                className="cancel-btn"
                              >
                                Anuluj
                              </button>
                            </div>
                          ) : (
                            <div className="action-buttons">
                              <button 
                                onClick={() => startEdit(feature.id, feature.name)}
                                className="edit-btn"
                              >
                                Edytuj
                              </button>
                              <button 
                                onClick={() => handleDelete('feature', feature.id)}
                                className="delete-btn"
                                disabled={loading}
                              >
                                Usuń
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-data">Brak cech dla wybranego typu produktu</div>
              )
            ) : (
              <div className="no-selection">Wybierz typ produktu, aby zobaczyć cechy</div>
            )}
          </>
        )}
        
        {activeTab === 'grapes' && (
          <>
            {loading ? (
              <div className="loading">Ładowanie...</div>
            ) : (
              grapes.length > 0 ? (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Nazwa</th>
                      <th>Akcje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grapes.map(grape => (
                      <tr key={grape.id}>
                        <td>
                          {editId === grape.id ? (
                            <input 
                              type="text" 
                              value={editName} 
                              onChange={(e) => setEditName(e.target.value)}
                              className="edit-input"
                            />
                          ) : (
                            grape.name
                          )}
                        </td>
                        <td>
                          {editId === grape.id ? (
                            <div className="action-buttons">
                              <button 
                                onClick={() => saveEdit('grape', grape.id)}
                                className="save-btn"
                                disabled={loading}
                              >
                                Zapisz
                              </button>
                              <button 
                                onClick={cancelEdit}
                                className="cancel-btn"
                              >
                                Anuluj
                              </button>
                            </div>
                          ) : (
                            <div className="action-buttons">
                              <button 
                                onClick={() => startEdit(grape.id, grape.name)}
                                className="edit-btn"
                              >
                                Edytuj
                              </button>
                              <button 
                                onClick={() => handleDelete('grape', grape.id)}
                                className="delete-btn"
                                disabled={loading}
                              >
                                Usuń
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="no-data">Brak odmian winogron</div>
              )
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CategoryManager;